import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { GlobalFunctions } from '../../../core/global-functions';
import { CONSTANTS } from '../../../core/constants';
import { LandingService } from '../landing.service';
import { ReviewService } from '../../../core/services/review.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-tutor-profile',
  standalone: true,
  imports: [CommonModule, RouterModule, TabViewModule, FormsModule, ToastModule],
  providers: [MessageService],
  templateUrl: './tutor-profile.component.html',
  styleUrls: ['./tutor-profile.component.scss']
})
export class TutorProfileComponent implements OnInit {
  tutor: any;
  baseImageUrl = CONSTANTS.baseImageUrl;
  isLoading = false;
  schedule: any[] = [];
  fullSchedule: any[] = [];
  startIndex = 0;
  isScheduleExpanded = false;
  canExpandSchedule = false;
  timeOfDayFilter: 'morning' | 'afternoon' | 'evening' | null = null;
  filteredSchedule: any[] = [];
  isPreviousButtonDisabled: boolean = true;
  isNextButtonDisabled: boolean = false;
  selectedSlot: any = null;
  selectedSubject: any = null;
  selectedDay: any = null;

  // Review State
  reviews: any[] = [];
  reviewsLoading = false;
  totalReviews = 0;
  reviewsPage = 1;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _globalFunctions: GlobalFunctions,
    private landingService: LandingService,
    private reviewService: ReviewService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const tutorId = this.route.snapshot.paramMap.get('id');
    if (tutorId) {
      this.isLoading = true;
      this.landingService.getTutorById(tutorId).subscribe({
        next: (result: any) => {
          if (result?.success) {
            this.tutor = result.data;
            this.reviews = this.tutor.reviews || [];
            this.totalReviews = this.tutor.totalReviews || 0;
            this.initializeSchedule();
            // this.fetchReviews(tutorId);
          } else {
            this._globalFunctions.successErrorHandling(result, this, true);
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          this._globalFunctions.errorHanding(error, this, true);
          this.isLoading = false;
        }
      });
    }
  }

  /** Initialize schedule from API data */
  private initializeSchedule(): void {
    if (!this.tutor?.booking_slots) {
      this.schedule = [];
      this.fullSchedule = [];
      return;
    }

    // Convert booking_slots object to sorted array
    const dateKeys = Object.keys(this.tutor.booking_slots).sort();
    this.fullSchedule = dateKeys.map(dateKey => ({
      date: new Date(dateKey),
      dayName: new Date(dateKey).toLocaleDateString('en-US', { weekday: 'long' }),
      timeSlots: this.tutor.booking_slots[dateKey]
    }));

    this.startIndex = 0;
    this.updateDisplaySchedule();
  }

  /** Update displayed schedule (7 days) */
  private updateDisplaySchedule(): void {
    this.schedule = this.fullSchedule.slice(this.startIndex, this.startIndex + 7);
    this.updateButtonStates();
    this.filterSchedule();
    
    this.canExpandSchedule = this.schedule.some(day => day.timeSlots.length > 10);
    this.isScheduleExpanded = false;
  }

  private updateButtonStates(): void {
    this.isPreviousButtonDisabled = this.startIndex === 0;
    this.isNextButtonDisabled = this.startIndex + 7 >= this.fullSchedule.length;
  }

  /** Move to next week */
  public nextWeek(): void {
    if (!this.isNextButtonDisabled) {
      this.startIndex += 7;
      this.updateDisplaySchedule();
    }
  }

  /** Move to previous week */
  public previousWeek(): void {
    if (!this.isPreviousButtonDisabled) {
      this.startIndex -= 7;
      this.updateDisplaySchedule();
    }
  }

  /** Toggle schedule expanded view */
  public toggleScheduleView(): void {
    this.isScheduleExpanded = !this.isScheduleExpanded;
  }

  getSubjectNames(): string {
    if (this.tutor && this.tutor.subjects) {
      return this.tutor.subjects.map((s: any) => s.name).join(', ');
    }
    return '';
  }

  /** Returns the fill percentage (0–100) for a star at starIndex given a rating.
   *  e.g. rating=3.6, starIndex=4 → 60  (fractional part × 100)
   */
  getStarFillPercent(starIndex: number, rating: number): number {
    if (rating >= starIndex) return 100;      // Fully filled
    if (rating < starIndex - 1) return 0;     // Completely empty
    return Math.round((rating % 1) * 100);    // Partial — e.g. 3.6 → 60
  }

  setTimeOfDayFilter(filter: 'morning' | 'afternoon' | 'evening' | null): void {
    this.timeOfDayFilter = filter;
    this.filterSchedule();
  }

  filterSchedule(): void {
    if (!this.timeOfDayFilter) {
      this.filteredSchedule = this.schedule;
      return;
    }

    this.filteredSchedule = this.schedule.map(day => {
      const filteredSlots = day.timeSlots.filter((slot: any) => {
        const hour = parseInt(slot.start_time.split(':')[0], 10);
        if (this.timeOfDayFilter === 'morning') {
          return hour >= 6 && hour < 12;
        }
        if (this.timeOfDayFilter === 'afternoon') {
          return hour >= 12 && hour < 18;
        }
        if (this.timeOfDayFilter === 'evening') {
          return hour >= 18 && hour < 24;
        }
        return true;
      });
      return { ...day, timeSlots: filteredSlots };
    });
  }

  public isSlotDisabled(day: any, slot: any): boolean {
    if (slot.status !== 'available') {
      return true;
    }

    const today = new Date();
    const dayDate = new Date(day.date);

    if (dayDate.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0)) {
      return true;
    }

    if (
      dayDate.getDate() === today.getDate() &&
      dayDate.getMonth() === today.getMonth() &&
      dayDate.getFullYear() === today.getFullYear()
    ) {
      const now = new Date();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();

      const slotTime = slot.start_time.split(':');
      const slotHours = parseInt(slotTime[0], 10);
      const slotMinutes = parseInt(slotTime[1], 10);

      if (slotHours < currentHours) {
        return true;
      }
      if (slotHours === currentHours && slotMinutes <= currentMinutes) {
        return true;
      }
    }

    return false;
  }

  selectSlot(day: any, slot: any): void {
    if (this.isSlotDisabled(day, slot)) return;
    this.selectedDay = day;
    this.selectedSlot = slot;
  }

  selectSubject(subject: any): void {
    this.selectedSubject = subject;
  }

  bookTrialLesson(): void {
    if (!this.selectedSlot || !this.selectedDay) {
        this.messageService.add({
            severity: 'warn',
            summary: 'Selection Required',
            detail: 'Please select a time slot first.'
        });
        return;
    }
    if (!this.selectedSubject) {
        this.messageService.add({
            severity: 'warn',
            summary: 'Subject Required',
            detail: 'Please select a subject you want to learn.'
        });
        return;
    }

    this.router.navigate(['/bookings/checkout'], {
        state: {
            tutor: this.tutor,
            slot: this.selectedSlot,
            subject: this.selectedSubject
        }
    });
  }

  fetchReviews(tutorId: string): void {
    this.reviewsLoading = true;
    this.reviewService.getTutorReviews(tutorId, { page: this.reviewsPage, limit: 10 })
      .pipe(finalize(() => this.reviewsLoading = false))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.reviews = res.data.reviews;
            this.totalReviews = res.data.pagination.total;
          }
        },
        error: (err) => console.error('Error fetching reviews:', err)
      });
  }
}